import {
  getAuthSession,
  getAuthSessionGeneration,
  getAuthSessionIdentity,
  subscribeAuthSession,
} from './authSession';

export type PermissionDecisionTarget = 'menu' | 'page' | 'action' | 'api';

export type FieldPermissionBehavior = 'show' | 'disable' | 'mask';

export interface PermissionGroupSummary {
  id?: string;
  code: string;
  name: string;
  preset?: boolean;
}

export interface FieldPermissionRule {
  fieldKey: string;
  fieldName?: string;
  behavior: FieldPermissionBehavior;
  resourceCode?: string;
  viewLevel?: 'FULL' | 'MASKED';
  editLevel?: 'EDITABLE' | 'READONLY';
  maskRuleCode?: string;
  maskText?: string;
  reason?: string;
}

export const FIELD_RESOURCES = {
  user: 'USER',
  member: 'MEMBER',
  registration: 'REGISTRATION',
  registrationParticipant: 'REGISTRATION_PARTICIPANT',
  registrationForm: 'REGISTRATION_FORM',
  activity: 'ACTIVITY',
  activityContact: 'ACTIVITY_CONTACT',
  notificationChannel: 'NOTIFICATION_CHANNEL',
  notificationRecord: 'NOTIFICATION_RECORD',
  notificationTask: 'NOTIFICATION_TASK',
} as const;

export const fieldPermissionKey = (resourceCode: string, fieldCode: string) =>
  `${resourceCode}.${fieldCode}`;

export interface PermissionSnapshotInput {
  source?: 'mock' | 'api' | 'fallback';
  userId?: string | number;
  permissionCodes?: string[];
  permissionGroups?: PermissionGroupSummary[];
  fieldPermissions?: Record<string, FieldPermissionRule>;
  updatedAt?: string;
}

export interface PermissionSnapshot {
  source: 'mock' | 'api' | 'fallback';
  userId?: string | number;
  permissionCodes: string[];
  permissionGroups: PermissionGroupSummary[];
  fieldPermissions: Record<string, FieldPermissionRule>;
  updatedAt: string;
}

export const PERMISSION_CODES = {
  workbenchPage: 'PAGE_WORKBENCH_HOME_DASHBOARD',
  systemSettingsMenu: 'MENU_SYSTEM_SETTINGS',
  organizationSettingsPage: 'PAGE_SYSTEM_SETTINGS_ORGANIZATION_SETTINGS',
  siteSettingsPage: 'PAGE_SYSTEM_SETTINGS_SITE_SETTINGS',
  wechatPaymentSettingsPage: 'PAGE_SYSTEM_SETTINGS_WECHAT_PAYMENT',
  wechatPaymentSettingsQuery: 'API_FINANCE_WECHAT_SETTINGS_QUERY',
  wechatPaymentSettingsConfigure: 'API_FINANCE_WECHAT_SETTINGS_CONFIGURE',
  siteDomainSettingsRead: 'API_SITE_DOMAIN_SETTINGS_READ',
  siteDomainSettingsUpdate: 'API_SITE_DOMAIN_SETTINGS_UPDATE',
  siteRegistrationSettingsRead: 'API_SITE_REGISTRATION_SETTINGS_READ',
  siteRegistrationSettingsUpdate: 'API_SITE_REGISTRATION_SETTINGS_UPDATE',
  siteRegistrationSettingsAuditRead:
    'API_SITE_REGISTRATION_SETTINGS_AUDIT_READ',
  siteAgreementRead: 'API_SITE_AGREEMENT_READ',
  siteAgreementUpdate: 'API_SITE_AGREEMENT_UPDATE',
  siteAgreementPublish: 'API_SITE_AGREEMENT_PUBLISH',
  aboutSectionRead: 'API_ABOUT_SECTION_READ',
  aboutSectionWrite: 'API_ABOUT_SECTION_WRITE',
  siteHelpRead: 'API_SITE_HELP_READ',
  siteHelpWrite: 'API_SITE_HELP_WRITE',
  userPermissionsPage: 'PAGE_SYSTEM_SETTINGS_USER_PERMISSIONS',
  userManagementPage: 'PAGE_SYSTEM_SETTINGS_USER_MANAGEMENT',
  systemDictionaryPage: 'PAGE_SYSTEM_SETTINGS_SYSTEM_DICTIONARY',
  integrationSettingsPage: 'PAGE_SYSTEM_SETTINGS_INTEGRATION_SETTINGS',
  wechatAppSettingsPage: 'PAGE_SYSTEM_SETTINGS_WECHAT_APP',
  wechatAppSettingsQuery: 'API_WECHAT_APP_SETTINGS_QUERY',
  wechatAppSettingsConfigure: 'API_WECHAT_APP_SETTINGS_CONFIGURE',
  printConfigurationPage: 'PAGE_SYSTEM_SETTINGS_PRINT_CONFIGURATION',
  auditLogsPage: 'PAGE_SYSTEM_SETTINGS_AUDIT_LOGS',
  auditQuery: 'API_SYSTEM_AUDIT_QUERY',
  tasksQueuesPage: 'PAGE_SYSTEM_SETTINGS_TASKS_QUEUES',
  systemTaskQuery: 'API_SYSTEM_TASK_QUERY',
  memberCenterMenu: 'MENU_MEMBER_CENTER',
  memberListPage: 'PAGE_MEMBER_CENTER_MEMBER_LIST',
  memberCertificationPage: 'PAGE_MEMBER_CERTIFICATION',
  memberCertificationView: 'API_MEMBER_CERTIFICATION_VIEW',
  memberCertificationReview: 'API_MEMBER_CERTIFICATION_REVIEW',
  memberCertificationGrant: 'API_MEMBER_CERTIFICATION_GRANT',
  memberCertificationRevoke: 'API_MEMBER_CERTIFICATION_REVOKE',
  memberCertificationConfig: 'API_MEMBER_CERTIFICATION_CONFIG',
  memberTagManagementPage: 'PAGE_MEMBER_CENTER_TAG_MANAGEMENT',
  memberTagRead: 'API_MEMBER_TAG_READ',
  memberTagDefinitionWrite: 'API_MEMBER_TAG_DEFINITION_WRITE',
  memberTagMemberWrite: 'API_MEMBER_TAG_MEMBER_WRITE',
  memberTagMemberImport: 'API_MEMBER_TAG_MEMBER_IMPORT',
  memberTagMemberExport: 'API_MEMBER_TAG_MEMBER_EXPORT',
  memberTagLifecycleWrite: 'API_MEMBER_TAG_LIFECYCLE_WRITE',
  memberTagAuditRead: 'API_MEMBER_TAG_AUDIT_READ',
  memberAudienceSegmentationPage: 'PAGE_MEMBER_CENTER_AUDIENCE_SEGMENTATION',
  memberAudienceRead: 'API_MEMBER_AUDIENCE_READ',
  memberAudienceUse: 'API_MEMBER_AUDIENCE_USE',
  memberAudienceManage: 'API_MEMBER_AUDIENCE_MANAGE',
  memberAudienceDownload: 'API_MEMBER_AUDIENCE_DOWNLOAD',
  memberAudienceAuditRead: 'API_MEMBER_AUDIENCE_AUDIT_READ',
  memberAudienceGlobalManage: 'API_MEMBER_AUDIENCE_GLOBAL_MANAGE',
  memberRelationsPage: 'PAGE_MEMBER_CENTER_MEMBER_RELATIONS',
  memberRelationsView: 'API_MEMBER_RELATIONS_VIEW',
  memberMaintenanceManage: 'API_MEMBER_MAINTENANCE_MANAGE',
  memberFollowupView: 'API_MEMBER_FOLLOWUP_VIEW',
  memberFollowupEdit: 'API_MEMBER_FOLLOWUP_EDIT',
  memberSupplyView: 'API_MEMBER_SUPPLY_VIEW',
  memberSupplyEdit: 'API_MEMBER_SUPPLY_EDIT',
  memberContributionRead: 'API_MEMBER_CONTRIBUTION_READ',
  memberContributionWrite: 'API_MEMBER_CONTRIBUTION_WRITE',
  memberQualificationRead: 'API_MEMBER_QUALIFICATION_READ',
  memberQualificationApprove: 'API_MEMBER_QUALIFICATION_APPROVE',
  memberQualificationRevoke: 'API_MEMBER_QUALIFICATION_REVOKE',
  memberNetworkMenu: 'MENU_MEMBER_NETWORK',
  memberNetworkPage: 'PAGE_MEMBER_NETWORK',
  memberNetworkView: 'API_MEMBER_NETWORK_VIEW',
  memberNetworkEdit: 'API_MEMBER_NETWORK_EDIT',
  memberNetworkManage: 'API_MEMBER_NETWORK_MANAGE',
  memberNetworkPhoneView: 'FIELD_MEMBER_NETWORK_PERSON_PHONE_VIEW_FULL',
  memberBenefitsPage: 'PAGE_MEMBER_CENTER_MEMBER_BENEFITS',
  memberSemiannualConfirmationPage:
    'PAGE_MEMBER_CENTER_SEMIANNUAL_CONFIRMATION',
  memberConfirmationQuery: 'API_MEMBER_CONFIRMATION_QUERY',
  memberConfirmationManage: 'API_MEMBER_CONFIRMATION_MANAGE',
  memberConfirmationLaunch: 'API_MEMBER_CONFIRMATION_LAUNCH',
  memberConfirmationClose: 'API_MEMBER_CONFIRMATION_CLOSE',
  memberConfirmationProxy: 'API_MEMBER_CONFIRMATION_PROXY',
  memberConfirmationRemind: 'API_MEMBER_CONFIRMATION_REMIND',
  memberConfirmationExport: 'API_MEMBER_CONFIRMATION_EXPORT',
  memberConfirmationAudience: 'API_MEMBER_CONFIRMATION_AUDIENCE',
  memberReportsPage: 'PAGE_MEMBER_CENTER_MEMBER_REPORTS',
  memberReportRead: 'API_MEMBER_REPORT_READ',
  memberReportDetail: 'API_MEMBER_REPORT_DETAIL',
  memberReportFinance: 'API_MEMBER_REPORT_FINANCE',
  memberReportExport: 'API_MEMBER_REPORT_EXPORT',
  memberReportRefresh: 'API_MEMBER_REPORT_REFRESH',
  activityCenterMenu: 'MENU_ACTIVITY_CENTER',
  activityListPage: 'PAGE_ACTIVITY_CENTER_ACTIVITY_LIST',
  activityFrontendOperationPage: 'PAGE_ACTIVITY_CENTER_FRONTEND_OPERATION',
  activityFrontendOperationRead: 'API_ACTIVITY_FRONTEND_OPERATION_READ',
  activityFrontendOperationWrite: 'API_ACTIVITY_FRONTEND_OPERATION_WRITE',
  activityWorkflowPage: 'PAGE_ACTIVITY_CENTER_ACTIVITY_WORKFLOW',
  activityProcessManage: 'API_ACTIVITY_PROCESS_MANAGE',
  activityVerificationAll: 'API_ACTIVITY_VERIFICATION_ALL',
  activityVerificationGrantManage: 'API_ACTIVITY_VERIFICATION_GRANT_MANAGE',
  activityProcessTemplateView: 'API_ACTIVITY_PROCESS_TEMPLATE_VIEW',
  activityProcessTemplateManage: 'API_ACTIVITY_PROCESS_TEMPLATE_MANAGE',
  registrationManagementPage: 'PAGE_ACTIVITY_CENTER_REGISTRATION_MANAGEMENT',
  registrationFormVersionRead: 'API_REGISTRATION_FORM_VERSION_READ',
  registrationFormVersionWrite: 'API_REGISTRATION_FORM_VERSION_WRITE',
  registrationFormVersionPublish: 'API_REGISTRATION_FORM_VERSION_PUBLISH',
  checkInManagementPage: 'PAGE_ACTIVITY_CENTER_CHECK_IN_MANAGEMENT',
  seatingManagementPage: 'PAGE_ACTIVITY_CENTER_SEATING_MANAGEMENT',
  labelPrintingPage: 'PAGE_ACTIVITY_CENTER_LABEL_PRINTING',
  labelTemplatesPage: 'PAGE_ACTIVITY_CENTER_LABEL_TEMPLATES',
  labelPublicTemplateManage: 'API_LABEL_PUBLIC_TEMPLATE_MANAGE',
  labelTemplateEdit: 'API_LABEL_TEMPLATE_EDIT',
  labelPrint: 'API_LABEL_PRINT',
  labelAccessManage: 'API_LABEL_ACCESS_MANAGE',
  activityNotificationPage: 'PAGE_ACTIVITY_CENTER_ACTIVITY_NOTIFICATION',
  activityLotteryPage: 'PAGE_ACTIVITY_CENTER_ACTIVITY_LOTTERY',
  activityLotteryGet: 'API_ACTIVITY_LOTTERY_GET',
  activityLotteryConfig: 'API_ACTIVITY_LOTTERY_CONFIG',
  activityLotteryDraw: 'API_ACTIVITY_LOTTERY_DRAW',
  activityLotteryClaim: 'API_ACTIVITY_LOTTERY_CLAIM',
  activityLotteryVoid: 'API_ACTIVITY_LOTTERY_VOID',
  activityLotteryCorrect: 'API_ACTIVITY_LOTTERY_CORRECT',
  activityLotteryClose: 'API_ACTIVITY_LOTTERY_CLOSE',
  activityLotteryReopen: 'API_ACTIVITY_LOTTERY_REOPEN',
  activityLotteryScreen: 'API_ACTIVITY_LOTTERY_SCREEN',
  activityLotteryExport: 'API_ACTIVITY_LOTTERY_EXPORT',
  activityLandingPage: 'PAGE_ACTIVITY_CENTER_ACTIVITY_LANDING_PAGE',
  activitySummaryPage: 'PAGE_ACTIVITY_CENTER_ACTIVITY_SUMMARY',
  activityAiPage: 'PAGE_ACTIVITY_AI',
  activityAiRead: 'API_ACTIVITY_AI_READ',
  activityAiGenerate: 'API_ACTIVITY_AI_GENERATE',
  financeCenterMenu: 'MENU_FINANCE_CENTER',
  paymentManagementPage: 'PAGE_FINANCE_CENTER_PAYMENT_MANAGEMENT',
  refundManagementPage: 'PAGE_FINANCE_CENTER_REFUND_MANAGEMENT',
  sponsorshipManagementPage: 'PAGE_FINANCE_CENTER_SPONSORSHIP_MANAGEMENT',
  materialManagementPage: 'PAGE_FINANCE_CENTER_MATERIAL_MANAGEMENT',
  invoiceManagementPage: 'PAGE_FINANCE_CENTER_INVOICE_MANAGEMENT',
  financialApprovalPage: 'PAGE_FINANCE_CENTER_FINANCIAL_APPROVAL',
  financialReportsPage: 'PAGE_FINANCE_CENTER_FINANCIAL_REPORTS',
  financialReportsRead: 'API_FINANCE_REPORT_READ',
  financialReportsDetail: 'API_FINANCE_REPORT_DETAIL',
  financialReportsExport: 'API_FINANCE_REPORT_EXPORT',
  financialReportsRefresh: 'API_FINANCE_REPORT_REFRESH',
  financeExpenseCategoryManage: 'API_FINANCE_EXPENSE_CATEGORY_MANAGE',
  financeArchive: 'API_FINANCE_ARCHIVE',
  financeArchiveQuery: 'API_FINANCE_ARCHIVE_QUERY',
  financeArchiveVerify: 'API_FINANCE_ARCHIVE_VERIFY',
  financeAttachment: 'API_FINANCE_ATTACHMENT',
  financeBenefitCarry: 'API_FINANCE_BENEFIT_CARRY',
  financeConfigure: 'API_FINANCE_CONFIGURE',
  financeCorrectionApply: 'API_FINANCE_CORRECTION_APPLY',
  financeCorrectionConfirm: 'API_FINANCE_CORRECTION_CONFIRM',
  financeCorrectionQuery: 'API_FINANCE_CORRECTION_QUERY',
  financeExpenseApply: 'API_FINANCE_EXPENSE_APPLY',
  financeExpenseEligibility: 'API_FINANCE_EXPENSE_ELIGIBILITY',
  financeExpenseQuery: 'API_FINANCE_EXPENSE_QUERY',
  financeExpenseRetry: 'API_FINANCE_EXPENSE_RETRY',
  financeExpenseReview: 'API_FINANCE_EXPENSE_REVIEW',
  financeExpenseTerminate: 'API_FINANCE_EXPENSE_TERMINATE',
  financeExpenseRecordManual: 'API_FINANCE_EXPENSE_RECORD_MANUAL',
  financeExport: 'API_FINANCE_EXPORT',
  financeMethodSwitch: 'API_FINANCE_METHOD_SWITCH',
  financeQueryChannel: 'API_FINANCE_QUERY_CHANNEL',
  financeReceiptConfirm: 'API_FINANCE_RECEIPT_CONFIRM',
  financeReceiptQuery: 'API_FINANCE_RECEIPT_QUERY',
  financeReceiptRecord: 'API_FINANCE_RECEIPT_RECORD',
  financeReconcile: 'API_FINANCE_RECONCILE',
  financeReconcileQuery: 'API_FINANCE_RECONCILE_QUERY',
  financeRefundApply: 'API_FINANCE_REFUND_APPLY',
  financeRefundConfirmException: 'API_FINANCE_REFUND_CONFIRM_EXCEPTION',
  financeRefundQuery: 'API_FINANCE_REFUND_QUERY',
  financeRefundRecordException: 'API_FINANCE_REFUND_RECORD_EXCEPTION',
  financeRefundRetry: 'API_FINANCE_REFUND_RETRY',
  financeRefundReview: 'API_FINANCE_REFUND_REVIEW',
  financeRepairResult: 'API_FINANCE_REPAIR_RESULT',
  inventoryAdjust: 'API_INVENTORY_ADJUST',
  inventoryIssue: 'API_INVENTORY_ISSUE',
  inventoryItemMaintain: 'API_INVENTORY_ITEM_MAINTAIN',
  inventoryQuery: 'API_INVENTORY_QUERY',
  inventoryCategoryMaintain: 'API_INVENTORY_CATEGORY_MAINTAIN',
  inventoryTransferApply: 'API_INVENTORY_TRANSFER_APPLY',
  inventoryTransferApprove: 'API_INVENTORY_TRANSFER_APPROVE',
  inventoryReceive: 'API_INVENTORY_RECEIVE',
  inventoryReturn: 'API_INVENTORY_RETURN',
  inventoryReturnSponsor: 'API_INVENTORY_RETURN_SPONSOR',
  inventorySettle: 'API_INVENTORY_SETTLE',
  sponsorAmend: 'API_SPONSOR_AMEND',
  sponsorArchive: 'API_SPONSOR_ARCHIVE',
  sponsorHandlerChange: 'API_SPONSOR_HANDLER_CHANGE',
  sponsorIntentReview: 'API_SPONSOR_INTENT_REVIEW',
  sponsorOrderCreate: 'API_SPONSOR_ORDER_CREATE',
  sponsorPartyMaintain: 'API_SPONSOR_PARTY_MAINTAIN',
  sponsorQuery: 'API_SPONSOR_QUERY',
  sponsorShip: 'API_SPONSOR_SHIP',
  aiManagementMenu: 'MENU_AI_MANAGEMENT',
  aiManagementConfigurationPage: 'PAGE_AI_MANAGEMENT_CONFIGURATION',
  aiManagementStatisticsPage: 'PAGE_AI_MANAGEMENT_STATISTICS',
  aiConfigurationRead: 'API_AI_CONFIGURATION_READ',
  aiConfigurationWrite: 'API_AI_CONFIGURATION_WRITE',
  aiPricingWrite: 'API_AI_PRICING_WRITE',
  aiProviderConnectivityTest: 'API_AI_PROVIDER_CONNECTIVITY_TEST',
  aiStatisticsRead: 'API_AI_STATISTICS_READ',
  aiStatisticsExport: 'API_AI_STATISTICS_EXPORT',
  notificationCenterMenu: 'MENU_NOTIFICATION_CENTER',
  notificationChannelManagementPage:
    'PAGE_NOTIFICATION_CENTER_CHANNEL_MANAGEMENT',
  notificationTemplatesPage: 'PAGE_NOTIFICATION_CENTER_NOTIFICATION_TEMPLATES',
  notificationSendPage: 'PAGE_NOTIFICATION_CENTER_NOTIFICATION_SEND',
  notificationTriggerStrategiesPage:
    'PAGE_NOTIFICATION_CENTER_TRIGGER_STRATEGIES',
  notificationSendRecordsPage: 'PAGE_NOTIFICATION_CENTER_SEND_RECORDS',
  notificationChannelRead: 'API_NOTIFICATION_CHANNEL_READ',
  notificationChannelWrite: 'API_NOTIFICATION_CHANNEL_WRITE',
  notificationChannelConnectivityTest:
    'API_NOTIFICATION_CHANNEL_CONNECTIVITY_TEST',
  notificationTemplateRead: 'API_NOTIFICATION_TEMPLATE_READ',
  notificationTemplateWrite: 'API_NOTIFICATION_TEMPLATE_WRITE',
  notificationTemplatePublish: 'API_NOTIFICATION_TEMPLATE_PUBLISH',
  notificationTemplateDelete: 'API_NOTIFICATION_TEMPLATE_DELETE',
  notificationTemplateTestSend: 'API_NOTIFICATION_TEMPLATE_TEST_SEND',
  notificationSystemTemplateWrite: 'API_NOTIFICATION_SYSTEM_TEMPLATE_WRITE',
  notificationSendPreview: 'API_NOTIFICATION_SEND_PREVIEW',
  notificationSendCreate: 'API_NOTIFICATION_SEND_CREATE',
  notificationTaskRead: 'API_NOTIFICATION_TASK_READ',
  notificationTaskCancel: 'API_NOTIFICATION_TASK_CANCEL',
  notificationRecordRead: 'API_NOTIFICATION_RECORD_READ',
  notificationRecordExport: 'API_NOTIFICATION_RECORD_EXPORT',
  notificationReportRead: 'API_NOTIFICATION_REPORT_READ',
  notificationEventPolicyRead: 'API_NOTIFICATION_EVENT_POLICY_READ',
  notificationEventPolicyWrite: 'API_NOTIFICATION_EVENT_POLICY_WRITE',
  notificationEventPolicyPublish: 'API_NOTIFICATION_EVENT_POLICY_PUBLISH',
  notificationEventPolicyToggle: 'API_NOTIFICATION_EVENT_POLICY_TOGGLE',
  notificationEventPolicyTest: 'API_NOTIFICATION_EVENT_POLICY_TEST',
  notificationEventExecutionRead: 'API_NOTIFICATION_EVENT_EXECUTION_READ',
  knowledgeBaseMenu: 'MENU_KNOWLEDGE_BASE',
  knowledgeAssetManagementPage: 'PAGE_KNOWLEDGE_BASE_ASSET_MANAGEMENT',
  knowledgeActivityMaterialsPage: 'PAGE_KNOWLEDGE_BASE_ACTIVITY_MATERIALS',
  knowledgeTemplateLibraryPage: 'PAGE_KNOWLEDGE_BASE_TEMPLATE_LIBRARY',
  knowledgeUserFeedbackPage: 'PAGE_KNOWLEDGE_BASE_USER_FEEDBACK',
  userFeedbackRead: 'API_USER_FEEDBACK_READ',
  taskApprovalMenu: 'MENU_TASK_APPROVAL',
  taskTodoCenterPage: 'PAGE_TASK_APPROVAL_TODO_CENTER',
  taskApprovalWorkflowPage: 'PAGE_TASK_APPROVAL_APPROVAL_WORKFLOW',
  taskExceptionTasksPage: 'PAGE_TASK_APPROVAL_EXCEPTION_TASKS',
  dataReportsMenu: 'MENU_DATA_REPORTS',
  operationsDashboardPage: 'PAGE_DATA_REPORTS_OPERATIONS_DASHBOARD',
  memberAnalysisPage: 'PAGE_DATA_REPORTS_MEMBER_ANALYSIS',
  activityAnalysisPage: 'PAGE_DATA_REPORTS_ACTIVITY_ANALYSIS',
  registrationCheckinAnalysisPage:
    'PAGE_DATA_REPORTS_REGISTRATION_CHECKIN_ANALYSIS',
  financeAnalysisPage: 'PAGE_DATA_REPORTS_FINANCE_ANALYSIS',
  notificationAnalysisPage: 'PAGE_DATA_REPORTS_NOTIFICATION_ANALYSIS',
  lotteryAnalysisPage: 'PAGE_DATA_REPORTS_LOTTERY_ANALYSIS',
  createPermissionGroup: 'API_PERMISSION_GROUP_CREATE',
  editPermissionGroup: 'API_PERMISSION_GROUP_UPDATE',
  deletePermissionGroup: 'API_PERMISSION_GROUP_DELETE',
  savePermissionGroupNodes: 'API_PERMISSION_GROUP_PERMISSIONS_SAVE',
  assignUserPermissionGroups: 'API_USER_ASSIGN_GROUPS',
  batchAssignUserPermissionGroups: 'API_USER_ASSIGN_GROUPS',
  createUser: 'API_USER_CREATE',
  editUser: 'API_USER_UPDATE',
  enableDisableUser: 'API_USER_UPDATE',
  deleteUser: 'API_USER_DELETE',
  resetUserPassword: 'API_USER_UPDATE',
  assignUserGroups: 'API_USER_ASSIGN_GROUPS',
  userListApi: 'API_USER_LIST',
  permissionGroupListApi: 'API_PERMISSION_GROUP_LIST',
  memberListApi: 'API_MEMBER_LIST',
  memberGetApi: 'API_MEMBER_GET',
  memberCreateApi: 'API_MEMBER_CREATE',
  memberImportApi: 'API_MEMBER_IMPORT',
  memberExportApi: 'API_MEMBER_EXPORT',
  memberUpdateApi: 'API_MEMBER_UPDATE',
  memberDeleteApi: 'API_MEMBER_DELETE',
  memberStatusUpdateApi: 'API_MEMBER_STATUS_UPDATE',
  memberFieldConfigReadApi: 'API_MEMBER_FIELD_CONFIG_READ',
  memberBenefitGrant: 'API_MEMBER_BENEFIT_GRANT',
  memberRegistrationGovernanceRead: 'API_MEMBER_REGISTRATION_GOVERNANCE_READ',
  memberRegistrationGovernanceHandle:
    'API_MEMBER_REGISTRATION_GOVERNANCE_HANDLE',
  memberRegistrationGovernanceAuditRead:
    'API_MEMBER_REGISTRATION_GOVERNANCE_AUDIT_READ',
  memberControlledSoftDelete: 'API_MEMBER_CONTROLLED_SOFT_DELETE',
  memberAdminPasswordReset: 'API_MEMBER_ADMIN_PASSWORD_RESET',
  memberThirdPartyUnbind: 'API_MEMBER_THIRD_PARTY_UNBIND',
} as const;

export const AUDIENCE_PERMISSION_CODES = {
  page: PERMISSION_CODES.memberAudienceSegmentationPage,
  read: PERMISSION_CODES.memberAudienceRead,
  use: PERMISSION_CODES.memberAudienceUse,
  manage: PERMISSION_CODES.memberAudienceManage,
  download: PERMISSION_CODES.memberAudienceDownload,
  auditRead: PERMISSION_CODES.memberAudienceAuditRead,
  globalManage: PERMISSION_CODES.memberAudienceGlobalManage,
} as const;

export const createPermissionSnapshot = (
  input: PermissionSnapshotInput = {},
): PermissionSnapshot => ({
  source: input.source ?? 'mock',
  userId: input.userId,
  permissionCodes: Array.from(new Set(input.permissionCodes ?? [])),
  permissionGroups: (input.permissionGroups ?? []).map((group) => ({
    ...group,
  })),
  fieldPermissions: Object.fromEntries(
    Object.entries(input.fieldPermissions ?? {}).map(([fieldKey, rule]) => [
      fieldKey,
      { ...rule, fieldKey: rule.fieldKey || fieldKey },
    ]),
  ),
  updatedAt: input.updatedAt ?? new Date().toISOString(),
});

export const createDefaultPermissionSnapshot = () =>
  createPermissionSnapshot({
    source: 'fallback',
  });

export const createReadonlyPermissionSnapshot = () =>
  createPermissionSnapshot({
    userId: 'mock-readonly-user',
    permissionCodes: [
      PERMISSION_CODES.systemSettingsMenu,
      PERMISSION_CODES.userPermissionsPage,
      PERMISSION_CODES.permissionGroupListApi,
    ],
    permissionGroups: [
      {
        id: '9002',
        code: 'readonly-auditor',
        name: '只读审计权限组',
      },
    ],
    fieldPermissions: {
      phone: {
        fieldKey: 'phone',
        fieldName: '手机号',
        behavior: 'mask',
        maskText: '***',
        reason: '测试快照仅允许手机号脱敏展示',
      },
      permissionGroupCode: {
        fieldKey: 'permissionGroupCode',
        fieldName: '权限组编码',
        behavior: 'disable',
        reason: '测试快照不允许编辑权限组编码',
      },
    },
  });

const permissionEvents = new EventTarget();
let permissionGeneration = 0;
export const getPermissionGeneration = () => permissionGeneration;
export const subscribePermissionSnapshot = (listener: () => void) => {
  permissionEvents.addEventListener('change', listener);
  return () => permissionEvents.removeEventListener('change', listener);
};
const publishPermissionChange = () => {
  permissionGeneration += 1;
  permissionEvents.dispatchEvent(new Event('change'));
};

const fallbackSnapshot = createDefaultPermissionSnapshot();
let permissionSnapshotResolver: () => PermissionSnapshot = () =>
  fallbackSnapshot;
let permissionSnapshotSession:
| Readonly<{ identity: string; generation: number }>
| undefined;

export type PermissionRecoveryStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'denied';
const recoveryEvents = new EventTarget();
const permissionOwnerKey = () =>
  `${getAuthSessionGeneration()}:${getAuthSessionIdentity() ?? ''}:${getAuthSession()?.currentUser?.id ?? ''}`;
let observedOwner = permissionOwnerKey();
let recoveryState: Readonly<{
  status: PermissionRecoveryStatus;
  ownerKey: string;
}> = {
  status: 'idle',
  ownerKey: observedOwner,
};
const publishRecoveryState = (status: PermissionRecoveryStatus) => {
  const ownerKey = permissionOwnerKey();
  if (recoveryState.status === status && recoveryState.ownerKey === ownerKey) return;
  recoveryState = { status, ownerKey };
  recoveryEvents.dispatchEvent(new Event('change'));
};

export const subscribePermissionRecovery = (listener: () => void) => {
  recoveryEvents.addEventListener('change', listener);
  return () => recoveryEvents.removeEventListener('change', listener);
};
export const getPermissionRecoveryState = () => {
  getAuthSession();
  return recoveryState;
};
export const getPermissionSnapshotSession = () => permissionSnapshotSession;

export const setPermissionSnapshotResolver = (
  resolver?: () => PermissionSnapshot,
) => {
  permissionSnapshotResolver = resolver ?? (() => fallbackSnapshot);
  permissionSnapshotSession = undefined;
  publishPermissionChange();
  publishRecoveryState(resolver ? 'ready' : 'idle');
};

const snapshotContent = (snapshot: PermissionSnapshot) =>
  JSON.stringify({
    ...snapshot,
    permissionCodes: [...snapshot.permissionCodes].sort(),
    permissionGroups: [...snapshot.permissionGroups].sort((a, b) =>
      a.code.localeCompare(b.code)),
    fieldPermissions: Object.fromEntries(
      Object.entries(snapshot.fieldPermissions).sort(([a], [b]) =>
        a.localeCompare(b)),
    ),
    updatedAt: undefined,
  });

export const setPermissionSnapshot = (snapshot: PermissionSnapshotInput) => {
  const normalizedSnapshot = createPermissionSnapshot(snapshot);
  const identity =
    normalizedSnapshot.source === 'api' ? getAuthSessionIdentity() : undefined;
  const publication = identity
    ? { identity, generation: getAuthSessionGeneration() }
    : undefined;
  const changed =
    snapshotContent(normalizedSnapshot) !==
      snapshotContent(permissionSnapshotResolver()) ||
    publication?.identity !== permissionSnapshotSession?.identity ||
    publication?.generation !== permissionSnapshotSession?.generation;
  permissionSnapshotResolver = () => normalizedSnapshot;
  permissionSnapshotSession = publication;
  if (changed) publishPermissionChange();
  publishRecoveryState(
    normalizedSnapshot.source === 'fallback' ? 'idle' : 'ready',
  );
};

export const resetPermissionSnapshotResolver = () => {
  const changed =
    permissionSnapshotResolver() !== fallbackSnapshot ||
    permissionSnapshotSession !== undefined;
  permissionSnapshotResolver = () => fallbackSnapshot;
  permissionSnapshotSession = undefined;
  if (changed) publishPermissionChange();
  publishRecoveryState('idle');
};

export const beginPermissionRecovery = () => {
  // Rechecking an already confirmed snapshot must not remount editors or discard drafts.
  if (recoveryState.status !== 'ready') publishRecoveryState('loading');
};
export const failPermissionRecovery = (denied = false) => {
  resetPermissionSnapshotResolver();
  publishRecoveryState(denied ? 'denied' : 'error');
};

subscribeAuthSession(() => {
  const owner = permissionOwnerKey();
  if (owner === observedOwner) return;
  observedOwner = owner;
  resetPermissionSnapshotResolver();
});

export const resolvePermissionSnapshot = () => {
  getAuthSession();
  return permissionSnapshotResolver();
};

const normalizeCodes = (permissionCodes: string | string[]) =>
  (Array.isArray(permissionCodes) ? permissionCodes : [permissionCodes]);

export const hasPermission = (
  snapshot: PermissionSnapshot,
  permissionCodes: string | string[],
) => {
  const codeSet = new Set(snapshot.permissionCodes);
  return normalizeCodes(permissionCodes).every((code) => codeSet.has(code));
};

export const hasAnyPermission = (
  snapshot: PermissionSnapshot,
  permissionCodes: string[],
) => {
  const codeSet = new Set(snapshot.permissionCodes);
  return permissionCodes.some((code) => codeSet.has(code));
};

export const hasAllPermissions = (
  snapshot: PermissionSnapshot,
  permissionCodes: string[],
) => hasPermission(snapshot, permissionCodes);

export const canAccessMenu = (snapshot: PermissionSnapshot, menuCode: string) =>
  hasPermission(snapshot, menuCode);

export const canAccessPage = (snapshot: PermissionSnapshot, pageCode: string) =>
  hasPermission(snapshot, pageCode);

export const canOperateAction = (
  snapshot: PermissionSnapshot,
  actionCode: string,
) => hasPermission(snapshot, actionCode);

export const canCallApi = (snapshot: PermissionSnapshot, apiCode: string) =>
  hasPermission(snapshot, apiCode);

export const getFieldPermissionRule = (
  snapshot: PermissionSnapshot,
  resourceCodeOrFieldKey: string,
  fieldCode?: string,
) =>
  snapshot.fieldPermissions[
    fieldCode
      ? fieldPermissionKey(resourceCodeOrFieldKey, fieldCode)
      : resourceCodeOrFieldKey
  ];

export const canViewFieldFull = (
  snapshot: PermissionSnapshot,
  resourceCodeOrFieldKey: string,
  fieldCode?: string,
) => {
  const rule = getFieldPermissionRule(
    snapshot,
    resourceCodeOrFieldKey,
    fieldCode,
  );
  if (!rule) {
    return false;
  }
  if (rule.viewLevel) {
    return rule.viewLevel === 'FULL';
  }
  return rule.behavior !== 'mask';
};

export const canEditField = (
  snapshot: PermissionSnapshot,
  resourceCodeOrFieldKey: string,
  fieldCode?: string,
) => {
  const rule = getFieldPermissionRule(
    snapshot,
    resourceCodeOrFieldKey,
    fieldCode,
  );
  if (rule?.editLevel) {
    return rule.editLevel === 'EDITABLE';
  }
  if (!rule) {
    return false;
  }
  const behavior = rule?.behavior;
  return behavior === 'show';
};

export const maskFieldValue = (
  snapshot: PermissionSnapshot,
  resourceCodeOrFieldKey: string,
  value: string | number | undefined | null,
  fieldCode?: string,
) => {
  const rule = getFieldPermissionRule(
    snapshot,
    resourceCodeOrFieldKey,
    fieldCode,
  );
  if (value === undefined || value === null) {
    return '';
  }

  if (!rule) {
    return '***';
  }

  if (rule.behavior !== 'mask') {
    return String(value);
  }

  const rawValue = String(value);
  if (rawValue.length <= 4) {
    return rule.maskText || '*'.repeat(rawValue.length);
  }

  return `${rawValue.slice(0, 3)}${rule.maskText || '****'}${rawValue.slice(-2)}`;
};

export const describePermissionDecision = (
  snapshot: PermissionSnapshot,
  target: PermissionDecisionTarget,
  code: string,
) => ({
  target,
  code,
  allowed: hasPermission(snapshot, code),
  source: snapshot.source,
  updatedAt: snapshot.updatedAt,
});
